import React, { ReactNode, useContext } from "react";
import { useTheme } from "styled-components";
import {
  AnalyticsPageName,
  LanguageNameSpaces,
  TradeExtraInfoGeneralMapData,
} from "@shared/types";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context.tsx";
import { ICON_TYPES } from "@shared/common/constants.ts";
import { TITLE_TO_VALUE_TO_HELPER_TOOLTIP } from "@shared/features/trades/common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types.ts";
import { formatDate } from "@shared/utils/date";
import TextWithIcon from "common/Text/TextWithIcon.tsx";
import {
  HelperTooltip,
  HelperTooltipProps,
} from "common/Tooltips/HelperTooltip.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { directionColor, getNewLookTokens } from "common/NewLook/tokens.ts";
import {
  DetailsEmpty,
  DetailsField,
  DetailsFields,
  DetailsLabel,
  DetailsSection,
  DetailsTitle,
  DetailsValue,
  DetailsValueText,
} from "features/trades/NewLook/drawerDetails.ts";
import NewLookTradeProgress from "features/trades/TradeDrawer/NewLookTradeProgress.tsx";
import { MINUS } from "features/trades/NewLook/useMoneyFormat.ts";
import { translateOptionName } from "@shared/common/i18n/optionNames";

interface NewLookTradeGeneralTabProps {
  data: TradeExtraInfoGeneralMapData | undefined;
}

/**
 * New look of the trade drawer's General tab: the trade's progress (PnL %,
 * time in trade and the large price ladder) leads, then the details as
 * hairline-separated groups of labels over figures.
 */
const NewLookTradeGeneralTab = ({ data }: NewLookTradeGeneralTabProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const { getTradeExtraInfoGeneralStatus, getTradeExtraInfoStatsStatus } =
    useContext(TradeDetailsContext)!;

  const getSectionData = (
    sectionKeys: (keyof TradeExtraInfoGeneralMapData)[]
  ) =>
    Object.entries(data || {})
      .filter(([key]) => sectionKeys.includes(key))
      .sort((a, b) => sectionKeys.indexOf(a[0]) - sectionKeys.indexOf(b[0]));

  const getParsedValue = (
    key: keyof TradeExtraInfoGeneralMapData,
    value: string | number | null
  ): ReactNode => {
    if (value === null || value === undefined || value === "")
      return <DetailsEmpty>{t("card.not_available")}</DetailsEmpty>;
    if (key === "Exchange") {
      return (
        <TextWithIcon
          iconType={ICON_TYPES.EXCHANGE}
          coinOrExchangeName={value?.toString()}
          text={value}
        />
      );
    }
    if (DATE_KEYS.includes(key)) return formatDate(value, true, true);
    if (PNL_KEYS.includes(key)) {
      const number = Number(value);
      return (
        <span style={{ color: directionColor(tokens, number) }}>
          {/* Keep the backend string (precision, no exponent notation); only
              swap its hyphen for a true minus sign ("-0.00" takes none). */}
          {String(value).replace(/^-/, number ? MINUS : "")}
        </span>
      );
    }
    if (key === "Long/Short" || key === "Margin Type")
      return translateOptionName(String(value));
    return value;
  };

  const keyToHelperData: Record<string, HelperTooltipProps> = {
    [POTENTIAL_PROFIT_KEY]: {
      analyticsPageName: AnalyticsPageName.TRADE_EXTRA_DETAILS,
      analyticsFieldName: POTENTIAL_PROFIT_KEY,
      description: t("general_tab.tooltips.potential_profit_description"),
      title: t("general_tab.tooltips.potential_profit_title"),
    },
  };

  const getValueHelper = (key: string, value: string | number | null) => {
    const helper =
      value !== null ? TITLE_TO_VALUE_TO_HELPER_TOOLTIP[key]?.[value] : null;
    if (!helper) return null;
    return (
      <HelperTooltip
        {...helper}
        description={tCommon(helper.description)}
        title={tCommon(helper.title)}
      />
    );
  };

  const loading =
    getTradeExtraInfoGeneralStatus.isLoading ||
    getTradeExtraInfoStatsStatus.isLoading ||
    !data;

  if (loading) return <GeneralTabSkeleton />;

  return (
    <>
      <NewLookTradeProgress />
      {Object.values(GeneralTabSections).map((section) => {
        const rows = getSectionData(GENERAL_TAB_SECTION_TO_KEYS[section]);
        if (!rows.length) return null;
        return (
          <DetailsSection key={section}>
            <DetailsTitle>{t(section)}</DetailsTitle>
            <DetailsFields>
              {rows.map(([name, value]) => (
                <DetailsField key={name}>
                  <DetailsLabel>
                    {t(FIELD_NAME_TO_TRANSLATION_KEY[name])}
                    {keyToHelperData[name] && (
                      <HelperTooltip {...keyToHelperData[name]} />
                    )}
                  </DetailsLabel>
                  <DetailsValue>
                    {getValueHelper(name, value)}
                    <DetailsValueText>
                      {getParsedValue(name, value)}
                    </DetailsValueText>
                  </DetailsValue>
                </DetailsField>
              ))}
            </DetailsFields>
          </DetailsSection>
        );
      })}
    </>
  );
};

export default NewLookTradeGeneralTab;

/** Loading: the progress block and two groups, shaped like the content. */
const GeneralTabSkeleton = () => (
  <div aria-busy={true}>
    <Skeleton width="100%" height={148} radius={12} />
    {[4, 6].map((count, sectionIndex) => (
      <DetailsSection key={sectionIndex}>
        <Skeleton width={120} height={14} style={{ marginBottom: 16 }} />
        <DetailsFields>
          {Array.from({ length: count }, (_, index) => (
            <DetailsField key={index}>
              <Skeleton width={72} height={10} />
              <Skeleton width={112} height={14} />
            </DetailsField>
          ))}
        </DetailsFields>
      </DetailsSection>
    ))}
  </div>
);

enum GeneralTabSections {
  ACCOUNT_STATUS = "general_tab.sections.account_status",
  TRADE_DETAILS = "general_tab.sections.trade_details",
  PROFIT_RISK_METRICS = "general_tab.sections.profit_risk_metrics",
}

const POTENTIAL_PROFIT_KEY = "Potential Profit";
const DATE_KEYS: string[] = ["Opened at"];
const PNL_KEYS: string[] = ["BTC PnL", "USD PnL"];

// Same fields and order as the classic General tab.
const FIELD_NAME_TO_TRANSLATION_KEY: Record<
  string,
  TranslationKeys<LanguageNameSpaces.TRADES>
> = {
  Account: "general_tab.fields.account",
  Exchange: "general_tab.fields.exchange",
  Status: "general_tab.fields.status",
  Source: "general_tab.fields.source",
  "Current Price": "general_tab.fields.current_price",
  "Opened at": "general_tab.fields.opened_at",
  "Entry Price": "general_tab.fields.entry_price",
  "Allocated Amount": "general_tab.fields.allocated_amount",
  "Filled Quantity": "general_tab.fields.filled_quantity",
  "Long/Short": "general_tab.fields.long_short",
  "Trade type": "general_tab.fields.trade_type",
  Leverage: "general_tab.fields.leverage",
  "Margin Type": "general_tab.fields.margin_type",
  "Estimated Liquidation Price":
    "general_tab.fields.estimated_liquidation_price",
  "BTC PnL": "general_tab.fields.btc_pnl",
  "USD PnL": "general_tab.fields.usd_pnl",
  "Risk/Reward": "general_tab.fields.risk_reward",
  [POTENTIAL_PROFIT_KEY]: "general_tab.fields.potential_profit",
};

const GENERAL_TAB_SECTION_TO_KEYS: Record<
  GeneralTabSections,
  (keyof TradeExtraInfoGeneralMapData)[]
> = {
  [GeneralTabSections.ACCOUNT_STATUS]: [
    "Account",
    "Exchange",
    "Status",
    "Source",
  ],
  [GeneralTabSections.TRADE_DETAILS]: [
    "Current Price",
    "Opened at",
    "Entry Price",
    "Allocated Amount",
    "Filled Quantity",
    "Long/Short",
    "Trade type",
    "Leverage",
    "Margin Type",
    "Estimated Liquidation Price",
  ],
  [GeneralTabSections.PROFIT_RISK_METRICS]: [
    "BTC PnL",
    "USD PnL",
    "Risk/Reward",
    POTENTIAL_PROFIT_KEY,
  ],
};
